import { useEffect, useRef } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { SmallParagraph } from '../typography/Typography'
import { ContactForm } from './ContactForm'

type Props = {
  open: boolean
  onClose: () => void
  // Catalog key of the chair being enquired about, stored as product_id.
  productId?: string
  productTitle?: string
  productImage?: string
  surface?: string
}

// How long the success message stays up before the sheet closes itself.
const CLOSE_AFTER_SUCCESS_MS = 2500

export const EnquiryModal = ({
  open,
  onClose,
  productId,
  productTitle,
  productImage,
  surface = 'product_detail',
}: Props) => {
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const onSuccess = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(onClose, CLOSE_AFTER_SUCCESS_MS)
  }

  return (
    <Dialog open={open} onClose={onClose} transition className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/40 transition duration-200 ease-out data-[closed]:opacity-0"
      />
      {/* Bottom sheet on mobile, centred dialog from md up. */}
      <div className="fixed inset-0 flex items-end justify-center md:items-center md:p-4">
        <DialogPanel
          transition
          className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl transition duration-300 ease-out data-[closed]:translate-y-full data-[closed]:opacity-0 md:max-h-[90vh] md:rounded-2xl md:data-[closed]:translate-y-0 md:data-[closed]:scale-95"
        >
          {/* Grab handle — mobile only, signals the sheet can be dismissed. */}
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-300 md:hidden" />

          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 pt-4 md:pt-5">
            <div className="flex items-center gap-3">
              {productImage && (
                <img
                  src={productImage}
                  alt={productTitle ?? ''}
                  className="size-14 shrink-0 rounded-lg border border-gray-100 object-cover"
                />
              )}
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  {productTitle ? `Enquire about ${productTitle}` : 'Enquire Now'}
                </DialogTitle>
                <SmallParagraph className="text-gray-500">
                  Tell us a bit about your project and our team will respond within 48 hours.
                </SmallParagraph>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-md p-1 text-gray-500 hover:bg-gray-100"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          <div className="overflow-y-auto px-6 pb-8 pt-6">
            <ContactForm
              productId={productId}
              // The chosen chair already says what type it is and where it goes.
              showProjectFields={false}
              surface={surface}
              onSuccess={onSuccess}
            />
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
